import * as Lambda from '../../services/LambdaApi';
import {
    PaymentMethodSetupResponse,
    PaymentPublicConfig,
    RequestChargePreviewRequest,
    RequestChargePreviewResponse,
    ResidentPaymentDisputeCreateResponse,
    ResidentPaymentTransactionsResponse,
    ResidentPaymentMethod,
    MembershipSubscription,
    MembershipPlanKey,
} from '../types/Payments';

const unwrapS2LContent = <T>(response: any): T => {
    if (response && response.Result && response.Result.Success === false) {
        throw new Error(response.Result.Error || 'Request failed');
    }

    if (response && response.Result && response.Result.Content !== undefined) {
        return response.Result.Content as T;
    }

    return response as T;
};

const normalizeTransactionsResponse = (
    response: any,
    page: number,
    pageSize: number,
): ResidentPaymentTransactionsResponse => {
    const content = unwrapS2LContent<any>(response) || {};
    const items = Array.isArray(content.items)
        ? content.items
        : Array.isArray(content.Result)
            ? content.Result
            : Array.isArray(content)
                ? content
                : [];

    const totalFromPagination = Number(content?.pagination?.totalCount);
    const totalFromLegacy = Number(content?.TotRecords);
    const totalRecords = Number.isFinite(totalFromPagination)
        ? totalFromPagination
        : Number.isFinite(totalFromLegacy)
            ? totalFromLegacy
            : items.length;

    return {
        items,
        TotRecords: totalRecords,
        pagination: content?.pagination || {
            page,
            pageSize,
            totalCount: totalRecords,
            totalPages: Math.max(1, Math.ceil(totalRecords / pageSize)),
        },
        summary: content?.summary,
        warnings: Array.isArray(content?.warnings) ? content.warnings : [],
    };
};

export const fetchPaymentsPublicConfig = async (facilityId: string): Promise<PaymentPublicConfig> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentPublicConfig`;
    const payload = { queryStringParameters: { facilityId } };
    const response = await Lambda.Get(apiPath, payload, 'lambda');
    return unwrapS2LContent<PaymentPublicConfig>(response);
};

export const fetchResidentPaymentMethods = async (
    residentId: string,
    facilityId: string,
    includeUsage = false,
): Promise<ResidentPaymentMethod[]> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentMethodsList`;
    const response = await Lambda.Post(apiPath, {
        residentId,
        facilityId,
        ...(includeUsage ? { includeUsage: true } : {}),
    }, 'lambda');
    return unwrapS2LContent<ResidentPaymentMethod[]>(response);
};

export const previewRequestInstanceCharge = async (
    payload: RequestChargePreviewRequest,
): Promise<RequestChargePreviewResponse> => {
    const facilityId = payload && payload.facilityId ? String(payload.facilityId) : '';
    if (!facilityId) {
        throw new Error('facilityId is required');
    }
    if (!payload || !payload.residentId) {
        throw new Error('residentId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentRequestPreview`;
    const response = await Lambda.Post(apiPath, payload, 'lambda');
    return unwrapS2LContent<RequestChargePreviewResponse>(response);
};

export const initializeResidentPaymentMethodSetup = async (
    residentId: string,
    facilityId: string,
): Promise<PaymentMethodSetupResponse> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentMethodsSetup`;
    const response = await Lambda.Post(apiPath, {
        residentId,
        facilityId,
    }, 'lambda');
    return unwrapS2LContent<PaymentMethodSetupResponse>(response);
};

export const confirmResidentPaymentMethodSetup = async (
    residentId: string,
    setupConfirmationId: string,
    facilityId: string,
    makeDefault = true,
    // The saved card this one replaces: the server ignores that card when checking for a duplicate,
    // so a renewed card with the same number can be added. The server also makes the new card the
    // default and removes the old one; the response says whether that removal worked.
    replacingMethodId?: string,
): Promise<ResidentPaymentMethod> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentMethodsConfirm`;
    const response = await Lambda.Post(apiPath, {
        residentId,
        setupConfirmationId,
        makeDefault,
        facilityId,
        ...(replacingMethodId ? { replacingMethodId } : {}),
    }, 'lambda');
    return unwrapS2LContent<ResidentPaymentMethod>(response);
};

export const setResidentDefaultPaymentMethod = async (
    residentId: string,
    methodId: string,
    facilityId: string,
): Promise<void> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentMethodsDefault`;
    const response = await Lambda.Post(apiPath, {
        residentId,
        methodId,
        facilityId,
    }, 'lambda');
    unwrapS2LContent<unknown>(response);
};

export const removeResidentPaymentMethod = async (
    residentId: string,
    methodId: string,
    facilityId: string,
): Promise<void> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentMethodsRemove`;
    const response = await Lambda.Post(apiPath, {
        residentId,
        methodId,
        facilityId,
    }, 'lambda');
    unwrapS2LContent<unknown>(response);
};

type FetchResidentTransactionsParams = {
    residentId: string;
    facilityId: string;
    page?: number;
    pageSize?: number;
    from?: string;
    to?: string;
    status?: string;
    sourceType?: string;
    residentDisputeStatus?: string;
};

export const fetchResidentPaymentTransactions = async ({
    residentId,
    facilityId,
    page = 1,
    pageSize = 10,
    from,
    to,
    status,
    sourceType,
    residentDisputeStatus,
}: FetchResidentTransactionsParams): Promise<ResidentPaymentTransactionsResponse> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentResidentTransactions`;
    const body: any = {
        residentId,
        facilityId,
        page,
        pageSize,
    };

    if (from) body.from = from;
    if (to) body.to = to;
    if (status) body.status = status;
    if (sourceType) body.sourceType = sourceType;
    if (residentDisputeStatus) body.residentDisputeStatus = residentDisputeStatus;

    const response = await Lambda.Post(apiPath, body, 'lambda');
    return normalizeTransactionsResponse(response, page, pageSize);
};

export const createResidentPaymentDispute = async (
    residentId: string,
    facilityId: string,
    transactionId: string,
    note: string,
): Promise<ResidentPaymentDisputeCreateResponse> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }
    if (!residentId) {
        throw new Error('residentId is required');
    }
    if (!transactionId) {
        throw new Error('transactionId is required');
    }
    if (!note || !note.trim()) {
        throw new Error('A dispute note is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentDisputeCreate`;
    const response = await Lambda.Post(apiPath, {
        residentId,
        facilityId,
        transactionId,
        note: note.trim(),
    }, 'lambda');

    return unwrapS2LContent<ResidentPaymentDisputeCreateResponse>(response);
};

// The one server answer that means "this resident has never subscribed" (a real answer, unlike a
// network or server failure). Matched on the whole message so a different error that merely
// mentions a subscription is never read as "not enrolled".
export const isMembershipSubscriptionNotFound = (error: unknown): boolean =>
    error instanceof Error && error.message.trim().toLowerCase() === 'membership subscription not found';

// Enh 4 -- planKey defaults server-side to 'membership' when omitted, so the
// existing guest-membership call sites (no planKey passed) are unaffected.
export const getMembershipSubscription = async (
    residentId: string,
    facilityId: string,
    planKey?: MembershipPlanKey,
): Promise<MembershipSubscription | null> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentMembershipGet`;
    const response = await Lambda.Post(apiPath, {
        residentId,
        subscriberId: residentId,
        facilityId,
        ...(planKey ? { planKey } : {}),
    }, 'lambda');
    return unwrapS2LContent<MembershipSubscription>(response);
};

export const createMembershipSubscription = async (
    residentId: string,
    facilityId: string,
    paymentMethodId?: string,
    planKey?: MembershipPlanKey,
    // The fee the resident saw before confirming. The server refuses (and names the new fee) if the
    // price changed in between, so nobody is charged an amount they did not see.
    expectedAmountCents?: number,
): Promise<MembershipSubscription> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentMembershipCreate`;
    const response = await Lambda.Post(apiPath, {
        residentId,
        subscriberId: residentId,
        facilityId,
        ...(paymentMethodId ? { paymentMethodId } : {}),
        ...(planKey ? { planKey } : {}),
        ...(typeof expectedAmountCents === 'number' ? { expectedAmountCents } : {}),
    }, 'lambda');
    return unwrapS2LContent<MembershipSubscription>(response);
};

export const updateMembershipSubscription = async (
    residentId: string,
    facilityId: string,
    payload: {
        paymentMethodId?: string;
        cancelAtPeriodEnd?: boolean;
    },
    planKey?: MembershipPlanKey,
): Promise<MembershipSubscription> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentMembershipUpdate`;
    const response = await Lambda.Post(apiPath, {
        residentId,
        subscriberId: residentId,
        facilityId,
        ...payload,
        ...(planKey ? { planKey } : {}),
    }, 'lambda');
    return unwrapS2LContent<MembershipSubscription>(response);
};

export const cancelMembershipSubscription = async (
    residentId: string,
    facilityId: string,
    planKey?: MembershipPlanKey,
): Promise<MembershipSubscription> => {
    if (!facilityId) {
        throw new Error('facilityId is required');
    }

    const apiPath = `/s2l/${facilityId}/paymentMembershipCancel`;
    const response = await Lambda.Post(apiPath, {
        residentId,
        subscriberId: residentId,
        facilityId,
        effective: 'period_end',
        ...(planKey ? { planKey } : {}),
    }, 'lambda');
    return unwrapS2LContent<MembershipSubscription>(response);
};
